import {
	AnimalPawIcon,
	ArrowLeftIcon,
	BackpackIcon,
	BookIcon,
	Button,
	ClassIcon,
	HomeIcon,
	NavMenu,
	NoteIcon,
	PuzzleIcon,
	ReadingListIcon,
	ScriptIcon,
	SpeciesIcon,
	SpellBookIcon,
	PopoverButton,
	ToastProvider,
	User,
} from '@kit';
import type {NavMenuItem} from '@kit';

import React, {Suspense, lazy, useState} from 'react';

import {SiteRoutes, useRoute} from '../lib/routes';
import {useToggle} from './utils/hooks';
import {cls} from './utils';
import {useUser} from './use-user';
import {Avatar} from './avatar';
import {useRulesTooltip} from './rules-tooltip';
import {GalleryProvider, GalleryViewer} from './gallery';
import {DiceProvider} from './dice';
import {SiteSearch} from './site-search';

import popoverCss from '@kit/popover-button/popover-button.module.css';
import css from './layout.module.css';

const LoginPopup = lazy(() => import('./login-popup').then((m) => ({default: m.LoginPopup})));

type Props = {
	children: React.ReactNode;
	/** на странице есть собственное поле поиска: второе в шапке не нужно */
	hideSearch?: boolean;
	handleBack?: () => void;
};

const menu: NavMenuItem[] = [
	{title: 'Главная', id: 'index', icon: <HomeIcon />, routes: ['index']},
	{
		title: 'Классы',
		id: 'classes',
		icon: <ClassIcon />,
		routes: [
			'class',
			'classes',
			'classInfo',
			'classFeature',
			'classSpells',
			'subClasses',
			'subClass',
			'classArcaneShots',
			'classArcaneShotFeature',
			'classInvocations',
			'classInvocationFeature',
			'classManeuvers',
			'classManeuverFeature',
			'classMetaMagics',
			'classMetaMagicFeature',
		],
	},
	{title: 'Виды', id: 'species', icon: <SpeciesIcon />, routes: ['specie', 'species', 'specieInfo', 'specieFeature']},
	{title: 'Черты', id: 'feats', icon: <PuzzleIcon />, routes: ['feat', 'feats']},
	{title: 'Предыстории', id: 'origins', icon: <ScriptIcon />, routes: ['origin', 'origins']},
	{title: 'Заклинания', id: 'spells', icon: <SpellBookIcon />, routes: ['spell', 'spells']},
	{title: 'Предметы', id: 'items', icon: <BackpackIcon />, routes: ['item', 'items']},
	{
		title: 'Чудовища',
		id: 'monsters',
		icon: <AnimalPawIcon />,
		routes: ['monster', 'monsters', 'monsterInfo', 'monsterVariant'],
	},
	{title: 'Глоссарий', id: 'glossary', icon: <ReadingListIcon />, routes: ['glossary', 'glossaryItem']},
	{title: 'Имена', id: 'names', icon: <NoteIcon />, routes: ['names', 'namesSet']},
	{title: 'Книги', id: 'books', icon: <BookIcon />, routes: ['books', 'book', 'bookChapter']},
];

export const Layout: React.FC<Props> = ({handleBack, hideSearch, children}) => {
	const routing = useRoute();
	const route = (routing?.routeName || 'index') as keyof SiteRoutes;
	const {logoutLoading, logout, ...user} = useUser();
	const [openLogin, toggleLogin] = useToggle(false);

	const [searchExpanded, setSearchExpanded] = useState(false);

	const backButton = (
		<div className={cls(css.backButton, handleBack && css.visibleBackButton)}>
			<Button onClick={handleBack} appearance="subtle" icon={<ArrowLeftIcon />} aria-label="Назад" />
		</div>
	);

	const {RulesTooltip, handleContentMouseOver, handleContentMouseOut} = useRulesTooltip();

	return (
		<ToastProvider>
		<DiceProvider>
		<GalleryProvider>
			<a href="#content" className={css.skipLink}>
				Перейти к содержимому
			</a>
			<div className={css.main}>
				<NavMenu items={menu} currentRoute={route} />
				<div className={css.content}>
					<header className={cls(css.appBar, searchExpanded && css.appBarSearching)}>
						{backButton}
						{hideSearch ? <div /> : <SiteSearch className={css.search} onExpandedChange={setSearchExpanded} />}
						<div className={css.authButtons}>
							{user.id ? (
								<PopoverButton
									hasChevron
									align="end"
									popover={
										<div className={popoverCss.popoverButtons}>
											<Button route="sheets" appearance="subtle" icon={<User />}>
												Бланки персонажей
											</Button>
											<Button route="account" appearance="subtle">
												Вход и безопасность
											</Button>
											<Button loading={logoutLoading} onClick={logout} appearance="subtle">
												Выйти
											</Button>
										</div>
									}
									appearance="subtle"
								>
									<Avatar user={user} />
								</PopoverButton>
							) : (
								<>
									{openLogin && (
										<Suspense fallback={null}>
											<LoginPopup onClose={toggleLogin} />
										</Suspense>
									)}
									<Button appearance="subtle" onClick={toggleLogin}>
										Вход
									</Button>
									<Button appearance="subtle" route="registration">
										Регистрация
									</Button>
								</>
							)}
						</div>
					</header>
					<main
						id="content"
						tabIndex={-1}
						className={css.contentInner}
						onMouseOver={handleContentMouseOver}
						onMouseOut={handleContentMouseOut}
					>
						{children}
					</main>
				</div>
			</div>
			<RulesTooltip />
			<GalleryViewer />
		</GalleryProvider>
		</DiceProvider>
		</ToastProvider>
	);
};
